/**
 * Admin: Create Business User – create account only. User must log in with credentials to complete profile.
 */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useAppDispatch, useUserReducer } from "../../Utility";
import { onPostUsers, onPostUsersReset } from "../../Store/Slices/userSlice";
import "./CreateBusinessUserPage.css";

function CreateBusinessUserPage() {
  const dispatch = useAppDispatch();
  const userReducer = useUserReducer();

  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    business_type: "venue",
  });
  const [createErrors, setCreateErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCreationSuccess, setShowCreationSuccess] = useState(false);

  const postUsersLoading = userReducer?.postUsersLoading ?? false;
  const postUsersStatusCode = userReducer?.postUsersStatusCode;
  const postUsersMessage = userReducer?.postUsersMessage;

  useEffect(() => {
    if (postUsersStatusCode === "201") {
      dispatch(onPostUsersReset());
      setShowCreationSuccess(true);
      toast.success("Business user created.");
    } else if (
      postUsersStatusCode === "400" ||
      postUsersStatusCode === 400 ||
      userReducer?.postUsersError
    ) {
      dispatch(onPostUsersReset());
      toast.error(postUsersMessage || "Create failed");
    } else if (postUsersStatusCode) {
      dispatch(onPostUsersReset());
      toast.error(postUsersMessage || "Create failed");
    }
  }, [
    postUsersStatusCode,
    postUsersMessage,
    userReducer?.postUsersError,
    dispatch,
  ]);

  const handleCreateAccountSubmit = (e) => {
    e.preventDefault();
    const e2 = {};
    if (!createForm.name?.trim()) e2.name = "Name is required";
    if (!createForm.email?.trim()) e2.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(createForm.email.trim()))
      e2.email = "Please enter a valid email address";
    if (!createForm.phone?.trim()) e2.phone = "Phone is required";
    else if (!/^(\+91|0)?[6-9]\d{9}$/.test(createForm.phone.replace(/\s+/g, "")))
      e2.phone = "Please enter a valid 10-digit phone number";
    if (!createForm.password?.trim()) e2.password = "Password is required";
    else if (createForm.password.length < 6)
      e2.password = "Password must be at least 6 characters";
    if (!createForm.confirmPassword?.trim())
      e2.confirmPassword = "Please confirm your password";
    else if (createForm.password !== createForm.confirmPassword)
      e2.confirmPassword = "Passwords do not match";
    if (!createForm.business_type) e2.business_type = "Select venue or vendor";
    setCreateErrors(e2);
    if (Object.keys(e2).length > 0) return;
    dispatch(
      onPostUsers({
        name: createForm.name.trim(),
        email: createForm.email.trim(),
        phone: createForm.phone.trim(),
        password: createForm.password,
        role: "business",
        business_type: createForm.business_type,
      })
    );
  };

  const setCreateFormField = (field, value) => {
    setCreateForm((p) => ({ ...p, [field]: value }));
    if (createErrors[field]) setCreateErrors((p) => ({ ...p, [field]: "" }));
  };

  return (
    <div className="admin-create-business-user admin-create-business-single">
      <div className="admin-page-header">
        <Link to="/admin/business-users" className="admin-back-link">
          ← Business Users
        </Link>
        <h1 className="admin-page-title">Create Business User</h1>
        <p className="admin-page-subtitle">
          {showCreationSuccess
            ? "Account created. Share the credentials with the user."
            : "Create an account for the business. The user can then log in to complete their profile."}
        </p>
      </div>

      <div className="admin-create-business-body admin-create-business-body-single">
        <div className="admin-step-content-card admin-create-business-card admin-create-business-form-card">
          {showCreationSuccess ? (
            <div className="admin-create-success-message">
              <h2 className="admin-create-success-title">Business user created</h2>
              <p className="admin-create-success-text">
                Login with the credentials to complete the profile.
              </p>
              <p className="admin-create-success-hint">
                Share the email and password with the user so they can sign in and complete their business profile.
              </p>
              <Link
                to="/admin/business-users"
                className="admin-form-btn admin-form-btn-submit"
              >
                Back to Business Users
              </Link>
            </div>
          ) : (
            <form onSubmit={handleCreateAccountSubmit} className="admin-data-form">
              <h2 className="admin-create-business-step-title">Create account</h2>
              <div className="admin-form-section">
                <h3 className="admin-form-section-title">Account details</h3>
                <div className="admin-form-row">
                  <div className="admin-form-group">
                    <label className="admin-form-label" htmlFor="create-name">Name *</label>
                    <input
                      id="create-name"
                      type="text"
                      className={`admin-form-input ${createErrors.name ? "admin-form-input-error" : ""}`}
                      value={createForm.name}
                      onChange={(e) => setCreateFormField("name", e.target.value)}
                      placeholder="Business / contact name"
                      autoComplete="name"
                    />
                    {createErrors.name && <span className="admin-form-error">{createErrors.name}</span>}
                  </div>
                  <div className="admin-form-group">
                    <label className="admin-form-label" htmlFor="create-email">Email *</label>
                    <input
                      id="create-email"
                      type="email"
                      className={`admin-form-input ${createErrors.email ? "admin-form-input-error" : ""}`}
                      value={createForm.email}
                      onChange={(e) => setCreateFormField("email", e.target.value)}
                      placeholder="Email"
                      autoComplete="email"
                    />
                    {createErrors.email && <span className="admin-form-error">{createErrors.email}</span>}
                  </div>
                </div>
                <div className="admin-form-row">
                  <div className="admin-form-group">
                    <label className="admin-form-label" htmlFor="create-phone">Phone *</label>
                    <input
                      id="create-phone"
                      type="tel"
                      className={`admin-form-input ${createErrors.phone ? "admin-form-input-error" : ""}`}
                      value={createForm.phone}
                      onChange={(e) => setCreateFormField("phone", e.target.value)}
                      placeholder="10-digit mobile number"
                      autoComplete="tel"
                    />
                    {createErrors.phone && <span className="admin-form-error">{createErrors.phone}</span>}
                  </div>
                  <div className="admin-form-group">
                    <label className="admin-form-label" htmlFor="create-business-type">Business type *</label>
                    <select
                      id="create-business-type"
                      className={`admin-form-input ${createErrors.business_type ? "admin-form-input-error" : ""}`}
                      value={createForm.business_type}
                      onChange={(e) => setCreateFormField("business_type", e.target.value)}
                    >
                      <option value="venue">Venue</option>
                      <option value="vendor">Vendor</option>
                    </select>
                    {createErrors.business_type && <span className="admin-form-error">{createErrors.business_type}</span>}
                  </div>
                </div>
              </div>
              <div className="admin-form-section">
                <h3 className="admin-form-section-title">Password</h3>
                <div className="admin-form-row">
                  <div className="admin-form-group admin-form-group-password">
                    <label className="admin-form-label" htmlFor="create-password">Password *</label>
                    <div className="admin-form-input-password-wrap">
                      <input
                        id="create-password"
                        type={showPassword ? "text" : "password"}
                        className={`admin-form-input ${createErrors.password ? "admin-form-input-error" : ""}`}
                        value={createForm.password}
                        onChange={(e) => setCreateFormField("password", e.target.value)}
                        placeholder="Min 6 characters"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="admin-form-password-toggle"
                        onClick={() => setShowPassword((p) => !p)}
                        tabIndex={-1}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                        )}
                      </button>
                    </div>
                    {createErrors.password && <span className="admin-form-error">{createErrors.password}</span>}
                  </div>
                  <div className="admin-form-group admin-form-group-password">
                    <label className="admin-form-label" htmlFor="create-confirm-password">Confirm password *</label>
                    <div className="admin-form-input-password-wrap">
                      <input
                        id="create-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        className={`admin-form-input ${createErrors.confirmPassword ? "admin-form-input-error" : ""}`}
                        value={createForm.confirmPassword}
                        onChange={(e) => setCreateFormField("confirmPassword", e.target.value)}
                        placeholder="Re-enter password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="admin-form-password-toggle"
                        onClick={() => setShowConfirmPassword((p) => !p)}
                        tabIndex={-1}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        title={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                        )}
                      </button>
                    </div>
                    {createErrors.confirmPassword && <span className="admin-form-error">{createErrors.confirmPassword}</span>}
                  </div>
                </div>
              </div>
              <div className="admin-form-actions">
                <Link to="/admin/business-users" className="admin-form-btn admin-form-btn-cancel">Cancel</Link>
                <button type="submit" className="admin-form-btn admin-form-btn-submit" disabled={postUsersLoading}>
                  {postUsersLoading ? "Creating…" : "Create"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateBusinessUserPage;
